import { ClientMessage, ConnectionError_Reason, ServerProtocolMessage } from "@inline-chat/protocol/core";
import { AsyncChannel } from "../utils/async-channel.js";
import { PingPongService } from "./ping-pong.js";
const emptyRpcInput = { oneofKind: undefined };
const defaultRpcTimeoutMs = 30_000;
const assertValidRpcMethod = (method) => {
    if (typeof method !== "number" || !Number.isInteger(method) || method <= 0) {
        throw new ProtocolClientError("invalid-rpc-method", { message: `Invalid rpc method: ${String(method)}` });
    }
};
export class ProtocolClient {
    events = new AsyncChannel();
    transport;
    pingPong;
    state = "connecting";
    log;
    getConnectionInit;
    defaultRpcTimeoutMs;
    pendingRpcRequests = new Map();
    seq = 0;
    lastTimestamp = 0;
    sequence = 0;
    epochSeconds = 1_735_689_600;
    connectionAttemptNo = 0;
    reconnectionTimer = null;
    authenticationTimeout = null;
    listenersStarted = false;
    lastConnectingAt = null;
    lastOpenAt = null;
    lastTransportMessageAt = null;
    lastFailureAt = null;
    lastFailureReason = null;
    constructor(options) {
        this.transport = options.transport;
        this.log = options.logger ?? {};
        this.getConnectionInit = options.getConnectionInit;
        this.defaultRpcTimeoutMs = normalizeRpcTimeoutMs(options.defaultRpcTimeoutMs, defaultRpcTimeoutMs);
        this.pingPong = new PingPongService({ logger: this.log });
        this.pingPong.configure(this);
        this.startListeners();
    }
    async startTransport() {
        await this.transport.start();
    }
    async stopTransport() {
        await this.transport.stop();
    }
    async sendPing(nonce) {
        const message = this.wrapMessage({
            oneofKind: "ping",
            ping: { nonce },
        });
        try {
            await this.transport.send(message);
        }
        catch (error) {
            this.log.error?.("Failed to send ping", error);
        }
    }
    async reconnect(options) {
        await this.transport.reconnect({
            skipDelay: options?.skipDelay,
            cause: options?.cause ?? this.lastFailureReason ?? "protocol-reconnect",
        });
    }
    async sendRpc(method, input = emptyRpcInput) {
        this.ensureOpenForRpc();
        assertValidRpcMethod(method);
        const message = this.wrapMessage({
            oneofKind: "rpcCall",
            rpcCall: { method, input },
        });
        await this.transport.send(message);
        return message.id;
    }
    async callRpc(method, input = emptyRpcInput, options) {
        assertValidRpcMethod(method);
        const message = this.wrapMessage({
            oneofKind: "rpcCall",
            rpcCall: { method, input },
        });
        return await new Promise((resolve, reject) => {
            const pending = {
                message,
                resolve,
                reject,
                timeoutMs: this.resolveRpcTimeoutMs(options?.timeoutMs),
                sending: false,
            };
            this.pendingRpcRequests.set(message.id, pending);
            if (pending.timeoutMs !== null) {
                pending.timeout = setTimeout(() => {
                    this.failPendingRpcRequest(message.id, new ProtocolClientError("timeout"));
                }, pending.timeoutMs);
            }
            this.trySendPendingRpcRequest(message.id);
        });
    }
    async startListeners() {
        if (this.listenersStarted)
            return;
        this.listenersStarted = true;
        (async () => {
            for await (const event of this.transport.events) {
                switch (event.type) {
                    case "connected":
                        await this.authenticate();
                        break;
                    case "message":
                        await this.handleTransportMessage(event.message);
                        break;
                    case "connecting":
                        await this.connecting();
                        break;
                    case "stopping":
                        await this.reset();
                        break;
                }
            }
        })().catch((error) => {
            this.log.error?.("Protocol client listener crashed", error);
        });
    }
    async handleTransportMessage(message) {
        this.lastTransportMessageAt = Date.now();
        switch (message.body.oneofKind) {
            case "connectionOpen":
                await this.connectionOpen();
                break;
            case "rpcResult":
                this.completeRpcResult(message.body.rpcResult.reqMsgId, message.body.rpcResult.result);
                await this.events.send({
                    type: "rpcResult",
                    msgId: message.body.rpcResult.reqMsgId,
                    rpcResult: message.body.rpcResult.result,
                });
                break;
            case "rpcError":
                this.completeRpcError(message.body.rpcError.reqMsgId, message.body.rpcError);
                await this.events.send({
                    type: "rpcError",
                    msgId: message.body.rpcError.reqMsgId,
                    rpcError: message.body.rpcError,
                });
                break;
            case "ack":
                await this.events.send({ type: "ack", msgId: message.body.ack.msgId });
                break;
            case "message":
                if (message.body.message.payload.oneofKind === "update") {
                    await this.events.send({ type: "updates", updates: message.body.message.payload.update });
                }
                else if (message.body.message.payload.oneofKind === "bot") {
                    await this.events.send({ type: "bot", bot: message.body.message.payload.bot });
                }
                break;
            case "pong":
                this.pingPong.pong(message.body.pong.nonce);
                break;
            case "connectionError":
                this.handleClientFailure(describeConnectionError(message.body.connectionError));
                break;
            default:
                break;
        }
    }
    async sendConnectionInit() {
        const connectionInit = this.getConnectionInit();
        if (!connectionInit)
            throw new ProtocolClientError("not-authorized");
        const message = this.wrapMessage({
            oneofKind: "connectionInit",
            connectionInit,
        });
        await this.transport.send(message);
    }
    async authenticate() {
        try {
            await this.sendConnectionInit();
            this.startAuthenticationTimeout();
        }
        catch (error) {
            this.log.error?.("Failed to authenticate", error);
            this.handleClientFailure(`authenticate failed: ${summarizeError(error)}`);
        }
    }
    async connectionOpen() {
        this.state = "open";
        this.lastOpenAt = Date.now();
        await this.events.send({ type: "open" });
        this.stopAuthenticationTimeout();
        if (this.reconnectionTimer) {
            clearTimeout(this.reconnectionTimer);
            this.reconnectionTimer = null;
        }
        this.connectionAttemptNo = 0;
        this.pingPong.start();
        this.resendPendingRpcRequests();
    }
    async connecting() {
        this.pingPong.stop();
        this.state = "connecting";
        this.lastConnectingAt = Date.now();
        await this.events.send({ type: "connecting" });
    }
    async reset() {
        this.pingPong.stop();
        this.stopAuthenticationTimeout();
        this.cancelAllPendingRpcRequests(new ProtocolClientError("stopped"));
        this.state = "connecting";
    }
    startAuthenticationTimeout() {
        this.stopAuthenticationTimeout();
        this.authenticationTimeout = setTimeout(() => {
            if (this.state === "open")
                return;
            this.handleClientFailure("authentication timeout after 10000ms");
        }, 10_000);
    }
    stopAuthenticationTimeout() {
        if (!this.authenticationTimeout)
            return;
        clearTimeout(this.authenticationTimeout);
        this.authenticationTimeout = null;
    }
    handleClientFailure(reason = "connection failure") {
        this.pingPong.stop();
        this.stopAuthenticationTimeout();
        this.state = "connecting";
        this.lastFailureAt = Date.now();
        this.lastFailureReason = reason;
        if (this.reconnectionTimer) {
            clearTimeout(this.reconnectionTimer);
        }
        this.connectionAttemptNo = (this.connectionAttemptNo + 1) >>> 0;
        const delayMs = Math.round(this.getReconnectionDelay() * 1000);
        this.log.warn?.(`Protocol reconnect scheduled (attempt=${this.connectionAttemptNo}, delayMs=${delayMs}, reason=${reason})`);
        this.reconnectionTimer = setTimeout(() => {
            if (this.state === "open")
                return;
            void this.reconnect({ skipDelay: true });
        }, delayMs);
    }
    getReconnectionDelay() {
        const attemptNo = this.connectionAttemptNo;
        if (attemptNo >= 8)
            return 8.0 + Math.random() * 5.0;
        return Math.min(8.0, 0.2 + Math.pow(attemptNo, 1.5) * 0.4);
    }
    wrapMessage(body) {
        this.advanceSeq();
        return ClientMessage.create({
            id: this.generateId(),
            seq: this.seq,
            body,
        });
    }
    advanceSeq() {
        this.seq = (this.seq + 1) >>> 0;
    }
    generateId() {
        const timestamp = this.currentTimestamp();
        if (timestamp === this.lastTimestamp) {
            this.sequence = (this.sequence + 1) >>> 0;
        }
        else {
            this.sequence = 0;
            this.lastTimestamp = timestamp;
        }
        return (BigInt(timestamp) << 32n) | BigInt(this.sequence);
    }
    currentTimestamp() {
        return Math.floor(Date.now() / 1000) - this.epochSeconds;
    }
    completeRpcResult(msgId, rpcResult) {
        const pending = this.getAndRemovePendingRpcRequest(msgId);
        pending?.resolve(rpcResult);
    }
    ensureOpenForRpc() {
        if (this.state !== "open") {
            throw new ProtocolClientError("not-connected");
        }
    }
    completeRpcError(msgId, rpcError) {
        const error = new ProtocolClientError("rpc-error", { code: rpcError.code, message: rpcError.message });
        const pending = this.getAndRemovePendingRpcRequest(msgId);
        pending?.reject(error);
    }
    failPendingRpcRequest(msgId, error) {
        const pending = this.getAndRemovePendingRpcRequest(msgId);
        pending?.reject(error);
    }
    getAndRemovePendingRpcRequest(msgId) {
        const pending = this.pendingRpcRequests.get(msgId);
        if (!pending)
            return null;
        if (pending.timeout)
            clearTimeout(pending.timeout);
        this.pendingRpcRequests.delete(msgId);
        return pending;
    }
    cancelAllPendingRpcRequests(error) {
        for (const pending of this.pendingRpcRequests.values()) {
            pending.reject(error);
            if (pending.timeout)
                clearTimeout(pending.timeout);
        }
        this.pendingRpcRequests.clear();
    }
    resolveRpcTimeoutMs(timeoutMs) {
        return normalizeRpcTimeoutMs(timeoutMs, this.defaultRpcTimeoutMs);
    }
    resendPendingRpcRequests() {
        for (const msgId of this.pendingRpcRequests.keys()) {
            this.trySendPendingRpcRequest(msgId);
        }
    }
    trySendPendingRpcRequest(msgId) {
        const pending = this.pendingRpcRequests.get(msgId);
        if (!pending)
            return;
        if (this.state !== "open")
            return;
        if (pending.sending)
            return;
        pending.sending = true;
        void this.transport
            .send(pending.message)
            .catch((error) => {
            this.log.warn?.("Failed to send RPC request; waiting for reconnect", error);
            this.handleClientFailure(`rpc send failed: ${summarizeError(error)}`);
        })
            .finally(() => {
            pending.sending = false;
        });
    }
    getDiagnostics() {
        return {
            state: this.state,
            connectionAttemptNo: this.connectionAttemptNo,
            pendingRpcCount: this.pendingRpcRequests.size,
            lastConnectingAt: this.lastConnectingAt,
            lastOpenAt: this.lastOpenAt,
            lastTransportMessageAt: this.lastTransportMessageAt,
            lastFailureAt: this.lastFailureAt,
            lastFailureReason: this.lastFailureReason,
            ping: this.pingPong.getDiagnostics(),
            transport: typeof this.transport.getDiagnostics === "function" ? this.transport.getDiagnostics() : null,
        };
    }
}
const normalizeRpcTimeoutMs = (timeoutMs, fallback) => {
    const resolved = timeoutMs === undefined ? fallback : timeoutMs;
    if (resolved == null)
        return null;
    if (resolved === Number.POSITIVE_INFINITY)
        return null;
    if (!Number.isFinite(resolved))
        return null;
    if (resolved <= 0)
        return null;
    return Math.floor(resolved);
};
export class ProtocolClientError extends Error {
    constructor(code, details) {
        super(details?.message ?? code);
        this.name = `ProtocolClientError:${code}`;
    }
}
function summarizeError(error) {
    if (error instanceof Error) {
        return `${error.name}: ${error.message}`;
    }
    return String(error);
}
function describeConnectionError(error) {
    const value = typeof error === "object" && error !== null ? error : null;
    const reason = typeof value?.reason === "number" ? value.reason : null;
    const reasonName = reason == null ? null : (ConnectionError_Reason[reason] ?? `UNKNOWN_REASON_${reason}`);
    const code = typeof value?.code === "number" ? value.code : null;
    const message = typeof value?.message === "string" && value.message.trim() ? value.message.trim() : (reasonName ?? "unknown");
    const suffix = reasonName != null ? ` (${reasonName})` : (code != null ? ` (code=${code})` : "");
    return `server connection error${suffix}: ${message}`;
}
